import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useListEventTypeCalendars } from '@cocoonspace/sdk-js/v2/domains/schedulers/schedulers'
import { Button } from '@cocoonspace/ui/components/button'
import { Skeleton } from '@cocoonspace/ui/components/skeleton'
import { useLingui } from '@lingui/react/macro'
import { Trash2Icon } from 'lucide-react'

import { GoogleCalendarIcon } from '~/components/icons/google-calendar-icon'
import { OutlookIcon } from '~/components/icons/outlook-icon'

const LoadingList = () => (
	<div className='flex flex-col gap-1'>
		{Array.from({ length: 3 }).map((_, index) => (
			<div
				key={index}
				className='flex items-center gap-2'
			>
				<Skeleton className='size-2' />
				<Skeleton className='h-3 w-[200px]' />
				<Skeleton className='ml-auto size-6' />
			</div>
		))}
	</div>
)

const CalendarItem = ({
	calendar,
	onRemove,
}: {
	calendar?: any
	onRemove: () => void
}) => {
	const { t } = useLingui()

	return (
		<div className='flex items-center gap-2 text-sm'>
			<div
				className='bg-muted size-2 rounded-full'
				style={{ backgroundColor: calendar?.color }}
			/>
			{calendar?.type === 'google' && <GoogleCalendarIcon className='size-4' />}
			{calendar?.type === 'outlook' && <OutlookIcon className='size-4' />}

			<span className='flex-1 truncate'>{calendar?.name}</span>

			<Button
				size='sm'
				variant='ghost'
				icon
				onClick={onRemove}
				aria-label={t`Retirer le calendrier ${calendar?.name ?? ''}`}
			>
				<Trash2Icon />
			</Button>
		</div>
	)
}

export const CalendarList = () => {
	const { t } = useLingui()

	const {
		data: calendars,
		isLoading,
		isError,
	} = useListEventTypeCalendars({
		query: {
			select: (data) => {
				return data.reduce(
					(acc, calendar) => {
						acc[calendar.id] = calendar
						return acc
					},
					{} as Record<string, any>,
				)
			},
		},
	})

	const form = useFormContext()

	const { fields, remove } = useFieldArray({
		control: form.control,
		name: 'conflictCalendarRefs',
	})

	if (isLoading) return <LoadingList />

	if (isError) {
		return (
			<div className='text-destructive text-sm'>
				{t`Une erreur est survenue lors de la récupération des calendriers`}
			</div>
		)
	}

	return (
		<div className='flex flex-col'>
			{!fields.length && (
				<div className='text-muted-foreground text-center text-sm'>
					{t`Aucun calendrier sélectionné`}
				</div>
			)}

			{fields?.map((field, index) => (
				<Controller
					key={field.id}
					control={form.control}
					name={`conflictCalendarRefs.${index}`}
					render={({ field }) => {
						return (
							<CalendarItem
								calendar={calendars?.[field.value.id as keyof typeof calendars]}
								onRemove={() => remove(index)}
							/>
						)
					}}
				/>
			))}
		</div>
	)
}
